import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import useStore from "../../../store/useStore";
import { getNotificationDestination } from "../../../utils/notificationNavigation";

/* ──────────────────────────────────────────────────────────────
   Helpers
────────────────────────────────────────────────────────────── */
const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: diffH > 48 * 7 ? "numeric" : undefined,
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ICON_MAP = {
  order: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
    </svg>
  ),
  payment: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  promo: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  ),
  delivery: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  ),
  stock: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.437L4 17h5m6 0a3 3 0 11-6 0" />
    </svg>
  ),
  default: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const COLOR_MAP = {
  order:    { bg: "bg-emerald-50/70",  icon: "bg-emerald-100 text-[#046a38]",  dot: "bg-[#046a38]", border: "border-l-[#046a38]" },
  payment:  { bg: "bg-blue-50/70",     icon: "bg-blue-100 text-blue-600",        dot: "bg-blue-500", border: "border-l-blue-500" },
  promo:    { bg: "bg-amber-50/70",    icon: "bg-amber-100 text-amber-600",      dot: "bg-amber-500", border: "border-l-amber-500" },
  delivery: { bg: "bg-violet-50/70",   icon: "bg-violet-100 text-violet-600",    dot: "bg-violet-500", border: "border-l-violet-500" },
  stock:    { bg: "bg-orange-50/70",   icon: "bg-orange-100 text-orange-600",    dot: "bg-orange-500", border: "border-l-orange-500" },
  default:  { bg: "bg-gray-50/70",     icon: "bg-gray-100 text-gray-500",        dot: "bg-gray-400", border: "border-l-gray-400" },
};

const detectType = (notification) => {
  const category = String(notification.category ?? notification.type ?? "").toLowerCase();
  if (category === "payment" || category === "refund") return "payment";
  if (["order", "delivery", "promo", "stock"].includes(category)) return category;
  const text = `${notification.type ?? ""} ${notification.title ?? ""} ${notification.message ?? ""}`.toLowerCase();
  if (/order|shipped|dispatch/.test(text)) return "order";
  if (/payment|paid|refund|cashfree/.test(text)) return "payment";
  if (/offer|promo|deal|discount|coupon/.test(text)) return "promo";
  if (/deliver|out.*for.*deliver|arrived/.test(text)) return "delivery";
  if (/stock|notify|back.*stock|available/.test(text)) return "stock";
  return notification.type ?? "default";
};

/* ──────────────────────────────────────────────────────────────
   NotificationCard
────────────────────────────────────────────────────────────── */
const NotificationCard = ({ notification, onRead }) => {
  const navigate = useNavigate();
  const type = detectType(notification);
  const colors = COLOR_MAP[type] ?? COLOR_MAP.default;
  const icon   = ICON_MAP[type]  ?? ICON_MAP.default;
  const isUnread = !notification.is_read;

  const handleCardClick = () => {
    if (isUnread) onRead(notification.id);
    const destination = getNotificationDestination(notification);
    if (destination) {
      if (/^https?:\/\//i.test(destination)) {
        window.location.assign(destination);
      } else {
        navigate(destination);
      }
      return;
    }
    navigate(`/account/notifications/${encodeURIComponent(notification.id)}`, {
      state: { notification },
    });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleCardClick();
        }
      }}
      className={`
        group relative flex items-start gap-3 sm:gap-4 p-3.5 sm:p-4 transition-all duration-150 cursor-pointer
        ${isUnread ? `${colors.bg} border-l-4 ${colors.border}` : "bg-white border-l-4 border-l-transparent"}
        hover:bg-gray-50 active:bg-gray-100
      `}
    >
      {/* Icon Badge */}
      <div
        className={`flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl sm:rounded-2xl ${colors.icon} shadow-xs`}
      >
        {icon}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className={`text-xs sm:text-sm font-bold truncate ${isUnread ? "text-gray-900" : "text-gray-700"}`}>
              {notification.title || "Notification"}
            </p>
            {isUnread && (
              <span className="flex h-2 w-2 shrink-0 rounded-full bg-[#046a38]" />
            )}
          </div>
          <span className="shrink-0 text-[10px] sm:text-xs font-semibold text-gray-400">
            {formatTime(notification.created_at || notification.timestamp)}
          </span>
        </div>

        <p className="mt-1 break-words text-xs sm:text-sm leading-relaxed text-gray-600">
          {notification.message || notification.body || ""}
        </p>

        {/* Quick action pill on mobile */}
        <div className="mt-2 flex items-center justify-between">
          {(type === "order" || type === "delivery") ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#046a38] group-hover:underline">
              <span>View Orders</span>
              <ArrowForwardIosIcon style={{ fontSize: 9 }} />
            </span>
          ) : type === "promo" ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 group-hover:underline">
              <span>Shop Deals</span>
              <ArrowForwardIosIcon style={{ fontSize: 9 }} />
            </span>
          ) : (
            <span />
          )}

          {isUnread && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#046a38] bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
              New
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   Skeleton Loader
────────────────────────────────────────────────────────────── */
const SkeletonRow = () => (
  <div className="flex gap-3 sm:gap-4 p-3.5 sm:p-4 animate-pulse">
    <div className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-xl bg-gray-200" />
    <div className="flex-1 space-y-2 pt-1">
      <div className="flex justify-between gap-4">
        <div className="h-3.5 w-2/5 rounded bg-gray-200" />
        <div className="h-3 w-16 rounded bg-gray-100" />
      </div>
      <div className="h-3 w-4/5 rounded bg-gray-100" />
      <div className="h-3 w-3/5 rounded bg-gray-100" />
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────
   Filter Tabs
────────────────────────────────────────────────────────────── */
const FILTERS = ["All", "Orders", "Payments", "Offers", "Stock"];

/* ──────────────────────────────────────────────────────────────
   Main Component
────────────────────────────────────────────────────────────── */
const NotificationsSection = () => {
  const navigate = useNavigate();
  const {
    serverNotifications,
    serverNotifications_loading,
    serverNotifications_error,
    serverNotifications_meta,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
  } = useStore();

  const [activeFilter, setActiveFilter] = useState("All");
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  // Initial load
  useEffect(() => {
    fetchNotifications({ page: 1, limit: LIMIT });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = useCallback(() => {
    setPage(1);
    fetchNotifications({ page: 1, limit: LIMIT });
  }, [fetchNotifications]);

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchNotifications({ page: nextPage, limit: LIMIT, append: true });
  }, [page, fetchNotifications]);

  const handleMarkAllRead = useCallback(async () => {
    await markAllNotificationsRead();
  }, [markAllNotificationsRead]);

  // Filter notifications by active tab
  const filtered = serverNotifications.filter((n) => {
    if (activeFilter === "All") return true;
    const type = detectType(n);
    if (activeFilter === "Orders")   return type === "order" || type === "delivery";
    if (activeFilter === "Payments") return type === "payment";
    if (activeFilter === "Offers")   return type === "promo";
    if (activeFilter === "Stock")    return type === "stock";
    return true;
  });

  const unreadCount = serverNotifications_meta?.unread_in_scope ??
    serverNotifications.filter((n) => !n.is_read).length;
  const hasMore = serverNotifications_meta
    ? serverNotifications_meta.has_more ?? page < serverNotifications_meta.total_pages
    : false;

  return (
    <div className="font-sans">
      {/* ── Header ── */}
      <div className="mb-4 sm:mb-5 flex items-center justify-between border-b border-gray-100 pb-3 sm:pb-4 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-base sm:text-xl font-black text-gray-900">
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#046a38] px-1.5 text-[10px] sm:text-[11px] font-bold text-white shadow-xs">
                {unreadCount}
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500 hidden sm:block">
            Updates about your orders, deliveries, and special offers.
          </p>
        </div>

        {/* Action buttons — compact & thumb-friendly */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={serverNotifications_loading}
            title="Refresh notifications"
            className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs font-bold text-gray-700 shadow-xs hover:bg-gray-50 active:scale-95 disabled:opacity-50 transition"
          >
            <RefreshIcon
              style={{ fontSize: 16 }}
              className={serverNotifications_loading ? "animate-spin" : ""}
            />
            <span className="hidden sm:inline">Refresh</span>
          </button>

          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="flex items-center gap-1 rounded-xl bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs font-bold text-[#046a38] hover:bg-emerald-100 active:scale-95 transition"
            >
              <DoneAllIcon style={{ fontSize: 16 }} />
              <span className="hidden sm:inline">Mark all read</span>
              <span className="sm:hidden">Read all</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Filter Tabs ── */}
      <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {FILTERS.map((f) => {
          const isActive = activeFilter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setActiveFilter(f)}
              className={`
                shrink-0 rounded-full px-3.5 py-1.5 text-xs font-bold transition-all active:scale-95
                ${
                  isActive
                    ? "bg-[#046a38] text-white shadow-sm"
                    : "border border-gray-200 bg-white text-gray-600 hover:border-[#046a38] hover:text-[#046a38]"
                }
              `}
            >
              {f}
            </button>
          );
        })}
      </div>

      {/* ── Error State ── */}
      {serverNotifications_error && !serverNotifications_loading && (
        <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3.5 text-xs text-red-700">
          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            {serverNotifications_error} —{" "}
            <button
              type="button"
              className="font-bold underline"
              onClick={handleRefresh}
            >
              Try again
            </button>
          </span>
        </div>
      )}

      {/* ── Notifications List Container ── */}
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        {/* Loading skeletons */}
        {serverNotifications_loading && serverNotifications.length === 0 ? (
          <div className="divide-y divide-gray-100">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center px-4 py-12 sm:py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-[#046a38] mb-3">
              <NotificationsNoneOutlinedIcon style={{ fontSize: 32 }} />
            </div>
            <p className="text-sm sm:text-base font-bold text-gray-800">
              {activeFilter === "All" ? "No notifications yet" : `No ${activeFilter.toLowerCase()} notifications`}
            </p>
            <p className="mt-1 text-xs text-gray-500 max-w-xs leading-relaxed">
              {activeFilter === "All"
                ? "You're all caught up! Order updates, delivery alerts and offers will appear here."
                : `Switch to "All" to view all your updates.`}
            </p>
            <button
              onClick={() => navigate("/products")}
              className="mt-4 rounded-xl bg-[#046a38] px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-[#03522b] transition active:scale-95"
            >
              Explore Fresh Groceries
            </button>
          </div>
        ) : (
          /* Notification rows */
          <div className="divide-y divide-gray-100" role="list" aria-label="Notifications">
            {filtered.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
                onRead={markNotificationRead}
              />
            ))}

            {/* Loading more indicator */}
            {serverNotifications_loading && serverNotifications.length > 0 && (
              <div className="divide-y divide-gray-100">
                <SkeletonRow />
                <SkeletonRow />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Load More ── */}
      {hasMore && !serverNotifications_loading && filtered.length > 0 && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleLoadMore}
            className="rounded-xl border-2 border-[#046a38] bg-white px-6 py-2.5 text-xs sm:text-sm font-bold text-[#046a38] shadow-sm transition hover:bg-emerald-50 active:scale-95"
          >
            Load More Notifications
          </button>
        </div>
      )}

      {/* ── Total count indicator ── */}
      {serverNotifications_meta && filtered.length > 0 && !serverNotifications_loading && (
        <p className="mt-3 text-center text-[11px] text-gray-400">
          Showing {filtered.length} of {serverNotifications_meta.total ?? serverNotifications.length} notifications
        </p>
      )}
    </div>
  );
};

export default NotificationsSection;
